import type { CSSProperties } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { ENTITY_TYPE } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import {
  formatInsightAsOf,
  formatInsightCount,
  formatInsightValue,
  insightDetailKey,
  insightRoleKey,
  insightWindowKey,
} from '../../../../lib/insight-card';
import type { InsightCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Card, Typography } from '../../ui';
import styles from './InsightCard.module.css';

/**
 * Measured numbers about the user's site, under the assistant reply that carries `metadata.insightCard`.
 * Every number comes from the server's copy of `site-insights.json`; nothing here computes one. It
 * renders no buttons: its two actions are the row's ordinary chips, minted from the same tool call.
 */
export const InsightCard = ({ card, conversationId }: { card: InsightCardMetadata; conversationId?: string }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const seenRef = useItemSeenBi('insight_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: { kinds: card.items.map((item) => item.kind).join(','), count: String(card.items.length) },
  });
  const asOf = formatInsightAsOf(card.computedAt, locale);

  return (
    <Card
      component="section"
      ref={seenRef}
      className={styles.card}
      aria-label={t('chat.insightCard.aria')}
      data-testid="chat-insight-card"
      data-count={card.items.length}
      style={{ '--tile-count': card.items.length } as CSSProperties}
    >
      <ul className={styles.tiles}>
        {card.items.map((item) => {
          const windowKey = insightWindowKey(item);
          const detailKey = insightDetailKey(item);
          const value = formatInsightValue(item, locale);
          return (
            <li key={item.id} className={styles.tile} data-kind={item.kind} data-role={item.agentRole}>
              <div className={styles.head}>
                <Typography variant="h4">{t(`chat.insightCard.kind.${item.kind}.title`)}</Typography>
                {windowKey && <span className={styles.window}>{t(windowKey, { days: item.windowDays })}</span>}
              </div>
              <span className={styles.role}>{t(insightRoleKey(item.agentRole))}</span>
              {value && <span className={styles.value}>{value}</span>}
              <span className={styles.label}>{t(`chat.insightCard.kind.${item.kind}.label`)}</span>
              {detailKey && (
                <p className={styles.detail}>{t(detailKey, { count: formatInsightCount(item, locale) ?? '' } as Record<string, unknown>)}</p>
              )}
            </li>
          );
        })}
      </ul>
      <p className={styles.asOf}>{t('chat.insightCard.asOf', { date: asOf })}</p>
    </Card>
  );
};
